import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { validateInput, validateInputChange } from "../../../functions/validateFunctions";
import './AddCategory.css';
import { submitCreateCategory, submitFile, submitEditCategory, submitEditFile} from '../../../redux/actions/docCategoryAction';
import { loadAlarmDeviceTypes, loadSpecificAlarmDevicesDetails } from '../../../redux/actions/alarmDeviceActions';
import CategoryDropdown from './/CategoryDropdown';
import permissions from '../../../permissionsList';


function CategoryFileManager(props) {
  const { onClose, onSave, mode = "CATEGORY", data = {}, isChild = false, onCancel, isEditing = false, catmoNodeId } = props;
   
  // Get user's access level for permission checks
  const userAccessLevel = useSelector(state => state.currentUser.preferences.access_level);
  
  // Simple permission check function
  const checkPermission = useCallback((feature) => {
    if (!feature || !feature.permissions) return false;
    return feature.permissions.includes(userAccessLevel);
  }, [userAccessLevel]);
  
  // Check if user has only associate devices permission (not full edit permission)
  const hasOnlyAssociatePermission = useMemo(() => {
    try {
      return checkPermission(permissions.static.DocAndImages.associateDevices) && 
             !checkPermission(permissions.static.DocAndImages.editDeleteCatOrFile);
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }, [checkPermission]);
   

 
 
  const fileUpload = useRef(null);
  const categoriesList = useSelector(state => state.docCategoryReducer.categoriesFiles)
  const [leftSearchValue, setLeftSearchValue] = useState('');
  const [rightSearchValue, setRightSearchValue] = useState('');
  const [selectedLeftItem, setSelectedLeftItem] = useState(null);
  const [selectedRightItem, setSelectedRightItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [isNameError, setIsNameError] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFileError, setIsFileError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const dispatch = useDispatch();
  const [currentFile, setCurrentFile] = useState(null);
  const [originalParentId, setOriginalParentId] = useState(null);
  const [selectedDeviceCategory, setSelectedDeviceCategory] = useState('');
  const [filterExcludedCategories, setFilterExcludedCategories] = useState(true);
  const saraNodeId = useSelector(state => state.docCategoryReducer.categoriesFiles.saraNodeId || state.docCategoryReducer.saraNodeId);
  const reduxCatmoNodeId = useSelector(state => state.docCategoryReducer.categoriesFiles.catmoNodeId || state.docCategoryReducer.catmoNodeId);
 
  // console.log("Redux state saraNodeId:", saraNodeId);
  // console.log("Redux state catmoNodeId:", reduxCatmoNodeId);
  
  const alarmDeviceList = useSelector(state => 
    Object.values(state.devices.alarmDeviceTypes)
  );

  const specificAlarmDevices = useSelector(state => 
    Object.values(state.devices.specificAlarmDevices)
  );
    
  const combinedDeviceList = useMemo(() => {
    const deviceList = [...alarmDeviceList];
    
    // Add specific devices if they exist
    if (specificAlarmDevices && specificAlarmDevices.length > 0) {
      // distinguish between device types and specific devices
      specificAlarmDevices.forEach(device => {
        if (device) {
          deviceList.push({
            ...device,
            isSpecificDevice: true 
          });
        }
      });
    }
    
    return deviceList;
  }, [alarmDeviceList, specificAlarmDevices]);


  // Used in CategoryDropdown to assign file to category
  const findParentCategory = (id, categories) => {  
    if (!id || !categories) return null;

    for (let obj of categories) {
        if (obj.id === id) return obj;
        if (obj.categories) {
            const category = findParentCategory(id, obj.categories);
            if (category) return category;
        }
    }
    return null;
};

// Function to check parent category's assigned devices
const getParentCategoryDevices = (categoryId) => {
  if (!categoryId || !categoriesList || !categoriesList.categories) return { deviceTypes: [], deviceIds: [] };
  
    // Function to find a category by ID
    const findCategory = (categories, id) => {
      if (!categories) return null;
    
    // Convert both to same type for comparison
    const targetId = typeof id === 'string' ? parseInt(id, 10) : id;
    
    for (const category of categories) {
      // Convert category id to number for consistent comparison
      const categoryId = typeof category.id === 'string' ? parseInt(category.id, 10) : category.id;
      
      if (categoryId === targetId) {
        // console.log(`Found matching category: ${category.name}`, category);
        return category;
      }
      
      if (category.categories) {
        const foundCategory = findCategory(category.categories, id);
        if (foundCategory) return foundCategory;
      }
    }
  return null;
};
  
  const parentCategory = findCategory(categoriesList.categories, categoryId);
  if (!parentCategory) return { deviceTypes: [], deviceIds: [] };
  

  
  const deviceTypes = parentCategory.alarm_device_types || [];
  const deviceIds = parentCategory.alarm_devices || [];
  
  return { deviceTypes, deviceIds };
 };

 // useEffect to automatically load inherited devices for files
 useEffect (() => {
  // run for file mode
  if (mode !== "FILE") return;

  // Need a parent category ID
  const currentParentId = isEditing 
    ? (selectedCategory ? selectedCategory.id : (data.parent || data.parentCategoryId))
    : (selectedCategory ? selectedCategory.id : null);
    
  // Skip if no parent ID or if we've already loaded devices
  if (!currentParentId) return;
  
  // Skip if no device list available
  if (!alarmDeviceList || alarmDeviceList.length === 0) return;

  if (isEditing && data.id) {
    // Check if the parent category has changed from the original
    const originalParentId = data.parent || data.parentCategoryId;
    const parentCategoryChanged = selectedCategory && selectedCategory.id !== originalParentId;
    
    // If parent category changed, inherit devices from new parent
    // Otherwise, check if file has its own device associations
    if (parentCategoryChanged) {
      console.log("Parent category changed, loading devices from new parent:", selectedCategory);
      // Skip to parent inheritance logic below
    } else {
      // Check if the file has its own device associations
      const hasOwnDeviceTypes = data.alarm_device_types && data.alarm_device_types.length > 0;
      const hasOwnDeviceIds = data.alarm_devices && data.alarm_devices.length > 0;
    
      // console.log("Editing file, checking for file's own devices:", hasOwnDeviceTypes, hasOwnDeviceIds);
    
      if (hasOwnDeviceTypes || hasOwnDeviceIds) {
        
        // Build device list from file's own device associations
        let fileDevices = [];
        
        // Process file's device types 
        const deviceTypes = data.alarm_device_types || data.alarmDeviceTypes || [];
        if (deviceTypes.length > 0) {

          const typeDevices = deviceTypes
            .map(typeId => {
              const device = alarmDeviceList.find(d => 
                d.alarm_device_type === typeId || d.type === typeId
              );
              if (!device) {

              }
              return device;
            })
            .filter(device => device);
          
          fileDevices = [...fileDevices, ...typeDevices];
        }
        
        // Process file's device IDs 
        const deviceIds = data.alarm_devices || data.alarmDevices || [];
        if (deviceIds.length > 0) {

          const idDevices = deviceIds
            .map(deviceId => {
              const device = specificAlarmDevices.find(d => 
                d.alarm_device_id === deviceId || d.id === deviceId
              );
              if (!device) {

              }
              if (device) {
                return {
                  ...device,
                  isSpecificDevice: true
                };
              }
              return null;
            })
            .filter(device => device);
          
          fileDevices = [...fileDevices, ...idDevices];
        }
        
        if (fileDevices.length > 0) {

          setSelectedItems(fileDevices);
          return; // Skip parent category device inheritance
        }
      }
    }
  }


    // Find the Parent Category
    const findCategory = (categories, id) => {
      for (const category of categories) {
        if (category.id === parseInt(id)) return category;
        if (category.categories) {
          const foundCategory = findCategory(category.categories, id);
          if (foundCategory) return foundCategory;
        }
      }
      return null;
    };
    
    if (!categoriesList || !categoriesList.categories) return;

    const parentCategory = findCategory(categoriesList.categories, currentParentId);
  
    if (parentCategory) {
     console.log("Found parent category:", parentCategory);
    
     // Check if the parent has device associations
    const deviceTypes = parentCategory.alarm_device_types ||parentCategory.alarmDeviceTypes || [];
    const deviceIds = parentCategory.alarm_devices || parentCategory.alarmDevices || [];
    
    if ((deviceTypes.length > 0 || deviceIds.length > 0) && alarmDeviceList.length > 0) {
      // console.log("Parent category has device associations:", { deviceTypes, deviceIds });
      
      // Convert device IDs to actual device objects
      let inheritedDevices = [];
      
      // Process device types
      if (deviceTypes.length > 0) {
        const typeDevices = deviceTypes
          .map(typeId => {
            return alarmDeviceList.find(d => 
              d.alarm_device_type === typeId || d.type === typeId
            );
          })
          .filter(device => device);
        
        inheritedDevices = [...inheritedDevices, ...typeDevices];
      }
      
      // Process device IDs
      if (deviceIds.length > 0) {
        const idDevices = deviceIds
          .map(deviceId => {
            const device = specificAlarmDevices.find(d => 
              d.alarm_device_id === deviceId || d.id === deviceId
            );
            if (device) {
              
              return {
                ...device,
                isSpecificDevice: true
              };
            }
            return null;

          })
          .filter(device => device);
        
        inheritedDevices = [...inheritedDevices, ...idDevices];
      }
      
      if (inheritedDevices.length > 0) {
        // console.log("Setting inherited devices from parent category:", parentCategory.name, inheritedDevices);
        setSelectedItems(inheritedDevices);
      } else {
        // Clear selected items if parent has no devices
        // console.log("Parent category has no devices, clearing selected items");
        setSelectedItems([]);
      }
      }  
    }
  
}, [selectedCategory, isEditing, data, data.parent, data.parentCategoryId, mode, alarmDeviceList.length, specificAlarmDevices.length]);



  useEffect(() => {
    dispatch(loadAlarmDeviceTypes());
    dispatch(loadSpecificAlarmDevicesDetails());
  }, [dispatch]);

  // console.log("CategoryFileManager props:", props);

  useEffect(() => {
    // console.log('useEffect triggered. data:', data, 'isEditing:', isEditing, 'mode:', mode);

    if (!alarmDeviceList || alarmDeviceList.length === 0) return;
    // console.log("Form initialization - data:", data);
    // console.log("Current name state:", name);
   if (data.id) {
    if (isEditing) {

      if (mode === "FILE") {
        const fileParentId = data.parentCategoryId || data.parent;
        setOriginalParentId(fileParentId);
        // console.log("Set original parent ID:", fileParentId);
        
        // Find and set the parent category
        const parentCategory = findParentCategory(fileParentId, categoriesList.categories);
        setSelectedCategory(parentCategory);

        
        // Set current file information
        // console.log('data.file:', data.name);
        if (data.name) {
          setCurrentFile(data.name);
        }
      } else {
        // For category editing, use data.parent
        setSelectedCategory(findParentCategory(data.parent, categoriesList.categories));
      }
      // Set form state for editing
      setName(data.name || '');
      setTitle(mode === "FILE" ? "Edit Document or Image" : "Edit Category");
      // setSelectedCategory(findParentCategory(data.parent, categoriesList.categories));
      
      // Array to add devices
      let combinedSelectedItems = [];
      
      // For alarm devices, find full device objects using IDs
      if (data.alarm_devices && data.alarm_devices.length > 0) {
        
        
        const selectedDevices = data.alarm_devices
          .map(deviceId => {
            const device = specificAlarmDevices.find(d => 
              d.alarm_device_id === deviceId || d.id === deviceId
            );
            
            if (!device) {
              // console.log("Could not find device with ID:", deviceId);
            }
            
            return device;
          })
          .filter(device => device);
        
        // console.log("Found selected devices:", selectedDevices);
        combinedSelectedItems = [...combinedSelectedItems, ...selectedDevices];
      }
      
      // For alarm device types, find full type objects using type IDs
      if (data.alarm_device_types && data.alarm_device_types.length > 0) {
        
        
        const selectedTypes = data.alarm_device_types
          .map(typeId => {
            const deviceType = alarmDeviceList.find(d => 
              d.alarm_device_type === typeId || d.type === typeId
            );
            
            if (!deviceType) {

            }
            
            return deviceType;
          })
          .filter(deviceType => deviceType);
        
        
        combinedSelectedItems = [...combinedSelectedItems, ...selectedTypes];
      }
      
      setSelectedItems(combinedSelectedItems);
      

     if (mode === "FILE" && data.file) {
      // console.log("Current file:", data.file);
      }
    } else if (!isChild) {
      setName(data.name || '');
      setTitle(mode === "FILE" ? "Update Document or Image" : "Update Category");
      setSelectedCategory(findParentCategory(data.parentId, categoriesList.categories));
    } else {
      setTitle(mode === "FILE" ? "Add Document or Image" : "Add Category");
      setSelectedCategory(findParentCategory(data.id, categoriesList.categories));
    }
  } else {
     setTitle(isChild ? "Add Subcategory" : (mode === "FILE" ? "Add Document or Image" : "Add Category"));
      // For new subcategories, default to the parent category
      if (data.defaultCategory) {
      // Use the passed default category for new files
      setSelectedCategory(data.defaultCategory);
    } else if (data.parent || data.parentId) {
      // Fallback to finding by parent ID
      const parentCategory = findParentCategory(data.parent || data.parentId, categoriesList.categories);
      setSelectedCategory(parentCategory);
    }
   }        
  }, [data, isChild, mode, categoriesList, isEditing]);




  const excludedCategories = useMemo(() => [
    1,2,4, 'Spare Devices', 'Every Type', 'POI', 'SPS', 'SPS - Zone Test Pendant', 'SPS - Zone Test Pendant - Safe',
    'SPS - New Zone Alarms'
  ], []);

  const excludedDeviceDescriptions = [
    'BATTERY ALARM',
    'SMOKE',
    'System',
    'CheckIn',
    'SS Monitor',
    'FIRE PANEL',
    'Fire Alarm',
    'Wander',
    'MISSING'
  ];


  // Helper function for category exclusions (handles both numeric and string categories)
  const isExcludedCategory = (device) => {
    // Get all possible category values from the device
    const categoryValues = [
      device.alarm_device_type,
      device.type,
      device.category, 
      device.deviceType 
    ].filter(val => val !== undefined && val !== null);

    // Normalize all values to lowercase strings for consistent comparison
    const normalizedDeviceValues = categoryValues.map(val => 
      String(val).trim().toLowerCase()
    );

    const normalizedExcluded = excludedCategories.map(cat =>
      String(cat).trim().toLowerCase()
    );

    // Check if any device category value is in the exclusion list
    let isExcluded = normalizedDeviceValues.some(deviceValue => 
      normalizedExcluded.includes(deviceValue)
    );

    // Also check if the device description contains any excluded string values
    if (!isExcluded) {
      const deviceDesc = (device.description || device.alarm_description || '').toLowerCase().trim();
      const stringExclusions = excludedCategories.filter(cat => typeof cat === 'string');
      
      // Check for exact matches first
      isExcluded = stringExclusions.some(excludedString => 
        deviceDesc === excludedString.toLowerCase().trim()
      );
      
      // Also check for substring matches for specific terms like "sps" and "poi"
      if (!isExcluded) {
        const substringExclusions = ['sps', 'poi']; // Add more terms here as needed
        isExcluded = substringExclusions.some(term => 
          deviceDesc.includes(term.toLowerCase())
        );
      }
    }

    return isExcluded;
  };

const hasExcludedDescription = (device) => {
  // Get all possible description fields
  const descriptions = [
    device.description,
    device.alarm_description,
    device.deviceDescription,
    device.name 
  ].filter(desc => desc && typeof desc === 'string');
  
  // Check if any description contains excluded terms
  return descriptions.some(description => {
    const descLower = description.toLowerCase().trim();
    
    // Check against excludedDeviceDescriptions list (case-insensitive substring)
    const hasExcludedFromList = excludedDeviceDescriptions.some(excludedDesc => 
      descLower.includes(excludedDesc.toLowerCase())
    );
    
    // Also check for specific substring exclusions like "sps" and "poi"
    const substringExclusions = ['sps', 'poi']; // Add more terms here as needed
    const hasSubstringExclusion = substringExclusions.some(term => 
      descLower.includes(term.toLowerCase())
    );
    
    return hasExcludedFromList || hasSubstringExclusion;
  });
};
 
  const getDeviceIdentifier = (device) => {
    if (!device) return 'undefined';
    
    if (device.isSpecificDevice) {
      return `specific:${device.alarm_device_id}`;
    }

    if (device.alarm_device_id) return `aid:${device.alarm_device_id}`;
    if (device.id) return `id:${device.id}`;
    
    
    const typeKey = device.alarm_device_type || device.type;
    const descKey = device.description || '';
    if (typeKey) return `type:${typeKey}:${descKey}`;
    
    // Last resort
    return `unknown:${Math.random()}`;
  };


  const handleTransfer = (device) => {
    if (!device) {
      console.error("Invalid device object");
      return;
    }
    
    try {
      const deviceId = getDeviceIdentifier(device);
      // console.log("Transfer for device with identifier:", deviceId);
      
      // Check if device is already selected
      const isAlreadySelected = selectedItems.some(item => {
        if (!item) return false;
        return getDeviceIdentifier(item) === deviceId;
      });
      
      const deviceCopy = { ...device };
    
      // isSpecificDevice flag 
      if (device.alarm_device_id) {
        deviceCopy.isSpecificDevice = true;
      }

      if (isAlreadySelected) {
        // Remove from selected items
        const newSelectedItems = selectedItems.filter(item => {
          if (!item) return false;
          return getDeviceIdentifier(item) !== deviceId;
        });
        // console.log("After removal, new selectedItems will be:", newSelectedItems);
        setSelectedItems(newSelectedItems);
      } else {
        // Add to selected items
        const newSelectedItems = [...selectedItems, device];
        // console.log("After addition, new selectedItems will be:", newSelectedItems);
        setSelectedItems(newSelectedItems);
      }
    } catch (error) {
      console.error("Error in handleTransfer:", error);
    }
  };
  

  const getVisibleDevices = () => {
    
    let visibleDevices = (combinedDeviceList || []).filter(device => device !== null && device !== undefined);
  
   // Filter out excluded category's/devices
   if (filterExcludedCategories) {
    // First, filter out excluded categories
    visibleDevices = visibleDevices.filter(device => !isExcludedCategory(device));
    
    // Then, filter out excluded descriptions
    visibleDevices = visibleDevices.filter(device => !hasExcludedDescription(device));
  }
  
   // Apply category filter if selected
   if (selectedDeviceCategory) {
    visibleDevices = visibleDevices.filter(device => 
      (device.alarm_device_type === selectedDeviceCategory) ||
      (device.type === selectedDeviceCategory)
     )
    
   }
  
   // Apply search filter if any
   if (leftSearchValue && leftSearchValue.trim() !== '') {
    const searchTerm = leftSearchValue.toLowerCase();
    visibleDevices = visibleDevices.filter(device => {
      const desc = device.description || device.alarm_description || '';
      return desc.toLowerCase().includes(searchTerm);
    });
   }
  
   // Filter out selected items
   if (selectedItems && selectedItems.length > 0) {
    visibleDevices = visibleDevices.filter(device => {

      if (!device) return false;
      const deviceId = getDeviceIdentifier(device);
      
      // Safely filter out selected items
      return !selectedItems.some(item => {
        if (!item) return false;
        return getDeviceIdentifier(item) === deviceId;
      });
    });
   }
  
   return visibleDevices;
  };

const handleMoveRight = () => {
  if (selectedLeftItem) {
    handleTransfer(selectedLeftItem);
    setSelectedLeftItem(null); // Clear selection after transfer
  }
};

const handleMoveLeft = () => {
  if (selectedRightItem) {
    handleTransfer(selectedRightItem);
    setSelectedRightItem(null); // Clear selection after transfer
  }
};

  const validateInputs = () => {
    if (!validateInput(name, setErrorMessage, "all")) {
      setIsNameError(true);
      setIsError(true);
      return false;
    }
    if (name.trim().length > 50) {
      const errorType = mode === "FILE" ? "Document/Image Name" : "Category name";
      setIsNameError(true);
      setErrorMessage(`${errorType} should not exceed 50 characters.`);
      setIsError(true);
      return false;
   }

   if (props.mode === "FILE" && !isEditing && (!file || fileUpload.current.value === '')) {
    setIsError(true);
    setIsFileError(true);
    setErrorMessage("Upload file");
    return false;
   }

   if (checkForDuplicates()) {
   const itemType = mode === "FILE" ? "Document/Image" : "Category";
   setIsNameError(true);
   setErrorMessage(`A ${itemType} with the name "${name}" already exists. Please choose a different name.`);
   setIsError(true);
   return false;
   }
   return true;
 };

  // Function to check if a file or category with the same name exists
 const checkForDuplicates = () => {
  // If we're editing, only check for duplicates with different IDs
  const currentId = data.id;
  
  if (mode === "CATEGORY") {
    // Search for duplicate categories

    let categoriesToCheck;
    
    if (selectedCategory) {
      // Creating/editing a subcategory - check siblings within the parent category
      categoriesToCheck = selectedCategory.categories || [];
    } else {
      // Creating/editing a top-level category - check top-level categories
      categoriesToCheck = categoriesList.categories;
    }
    return checkDuplicateCategory(categoriesToCheck, name, currentId);
  } else {
    // Search for duplicate files in the current category
    const parentCategory = selectedCategory ? selectedCategory : 
                           findParentCategory(data.parent, categoriesList.categories);
    
    if (parentCategory) {
      return checkDuplicateFile(parentCategory, name, currentId);
    }
    
    // Also check uncategorized files
    if (categoriesList.uncategorizedFiles) {
      return categoriesList.uncategorizedFiles.some(file => 
        file.name.toLowerCase() === name.toLowerCase() && file.id !== currentId
      );
    }
  }
  
  return false;
};

// Helper function to recursively check for duplicate categories
const checkDuplicateCategory = (categories, categoryName, currentId) => {
  if (!categories || !Array.isArray(categories)) return false;
  
  // Only check for duplicates at the current level
  return categories.some(category => 
    category.name.toLowerCase() === categoryName.toLowerCase() && category.id !== currentId
  );
};

// Helper function to check for duplicate files
const checkDuplicateFile = (category, fileName, currentId) => {
  if (!category) return false;
  
  // Check files in this category
  if (category.files) {
    const fileExists = category.files.some(file => 
      file.name.toLowerCase() === fileName.toLowerCase() && file.id !== currentId
    );
    
    if (fileExists) return true;
  }
  
  return false;
};

  const processInputs = () => {
    // console.log ("parent id", data.parent)
    const existingDocType = data.doc_type || 
          (data.files && data.files[0] && data.files[0].doc_type) || 
          undefined;


   let parentId = null;
    // let fileDocType = null;
    
    
    if (isEditing) {
      // For editing, check if a new category has been selected
      if (selectedCategory && selectedCategory.id) {
        // Use the newly selected category if one is chosen
        parentId = selectedCategory.id;
      } else if (selectedCategory === null) {
        // Explicitly selected "uncategorized"
        if (mode === "FILE") {
          // Files can be uncategorized (null parent)
          parentId = null;
        } else {
          // Categories must always have a parent - use appropriate node based on Catmo lineage
          if (hasCatmoLineage) {
            // Catmo categories stay under Catmo node when uncategorized
            parentId = reduxCatmoNodeId || catmoNodeId;
          } else {
            // Sara categories stay under Sara node when uncategorized
            parentId = saraNodeId;
          }
        }
      } else {
        // No category selection change, preserve the original parent
        parentId = data.parent || data.parentCategoryId;
      }
    } else if (selectedCategory && selectedCategory.id) {
      // For new items, use selected category if one is selected
      parentId = selectedCategory.id;
    } else if (mode === "FILE") {
      // For uncategorized files, use appropriate node based on lineage
      if (hasCatmoLineage) {
        // Catmo files stay under Catmo node when uncategorized
        parentId = reduxCatmoNodeId || catmoNodeId;
      } else {
        // Sara files use Sara node ID for API compatibility
        // The middleware will handle converting this to uncategorized in Redux
        parentId = saraNodeId;
      }
    } else if (mode === "CATEGORY") {
      // For new categories without a selected parent, use appropriate node
      if (hasCatmoLineage) {
        // Catmo categories under Catmo node
        parentId = reduxCatmoNodeId || catmoNodeId;
      } else {
        // Sara categories under Sara node
        parentId = saraNodeId;
      }
    }
    // If no saraNodeId available, parentId remains null
  
 
  //  console.log("Processed input with parent:", selectedCategory);
  //  console.log("Processing selectedItems:", selectedItems);

   const deviceTypes = [];
   const deviceIds = [];
   
   selectedItems.forEach(item => {
    

    // Check if it's a specific device or a device type
    if (item.isSpecificDevice === true || item.alarm_device_id) {
      // This is a specific device - add to deviceIds
      const deviceId = item.alarm_device_id || item.id;
      if (deviceId) {
        // console.log("Adding device ID:", deviceId);
        deviceIds.push(deviceId);
      }
    } else {
      // This is a device type - add to deviceTypes
      const deviceType = item.alarm_device_type || item.type;
      if (deviceType) {
        // console.log("Adding device type:", deviceType);
        deviceTypes.push(deviceType);
      }
    }
  });
    

    // Get parent category's devices for comparison
    let inheritedDeviceTypes = [];
    let inheritedDeviceIds = [];
  
    if (mode === "FILE" && parentId) {
    const parentDevices = getParentCategoryDevices(parentId);
    inheritedDeviceTypes = parentDevices.deviceTypes || [];
    inheritedDeviceIds = parentDevices.deviceIds || [];
    }
  
    // Checks selected devices with inherited devices
   const areDeviceTypesSame = areArraysEqual(deviceTypes, inheritedDeviceTypes);
   const areDeviceIdsSame = areArraysEqual(deviceIds, inheritedDeviceIds);
  
    let mimeType = null;
    const uploadedFile = fileUpload.current?.files[0];
    
    if (uploadedFile) {
      // Use the browser's built-in type detection
      mimeType = uploadedFile.type;
      // console.log("Detected MIME type from uploaded file:", mimeType);
    } else if (isEditing && data) {
      // For existing files being edited, check all possible mime type properties
      mimeType = data.mime_content_type || data.mimeType || data.mimetype;
      // console.log("Using existing MIME type from data:", mimeType);
      
      // If no mime type found in data, try to detect from name/URL
      if (!mimeType && (data.file || data.name)) {
        const getFileType = (filename, url) => {
          const sources = [filename, url].filter(Boolean);
        
        for (const source of sources) {
          if (!source) continue;
          
          const extension = source.toLowerCase().split('.').pop();
          
          switch(extension) {
            case 'pdf': return 'application/pdf';
            case 'jpg':
            case 'jpeg': return 'image/jpeg';
            case 'png': return 'image/png';
            case 'gif': return 'image/gif';
            default: continue;
          }
        }
        
        return 'application/octet-stream';
      };
      
      mimeType = getFileType(data.name, data.file);

    }
  } 
  
    const dataInput={ 
      id: data.id,
      name: name,
      parent: parentId,
      file: uploadedFile || null,
      docType: existingDocType || (mode === "FILE" ? 'media-file' : 'dir'),
      mimeType: mimeType,
      object_id: data.object_id,
      hasCatmoLineage: hasCatmoLineage, // Pass Catmo lineage flag to middleware
      catmo_id: data.catmo_id, // Also pass catmo_id if available
      
    };
    console.log ("DataInput", dataInput)

    // Only include device types and ids if they are different from inherited ones
    // or if this is a category
    if (mode !== "FILE" || !areDeviceTypesSame) {
    dataInput.alarmDeviceTypes = [...new Set(deviceTypes)];
    }
  
    if (mode !== "FILE" || !areDeviceIdsSame) {
    dataInput.alarmDevices = deviceIds;
    }
    
    if (fileUpload.current && fileUpload.current.files && fileUpload.current.files.length > 0) {
      dataInput.file = fileUpload.current.files[0];
    }
    return dataInput;  
  };


  // Helper function to compare arrays
  const areArraysEqual = (array1, array2) => {
  if (array1.length !== array2.length) return false;
  
  // Sort both arrays to ensure same order for comparison
  const sortedArray1 = [...array1].sort();
  const sortedArray2 = [...array2].sort();
  
  return sortedArray1.every((value, index) => value === sortedArray2[index]);
};

const setStateFunc = (response) => {
  // console.log("setStateFunc called with response:", response);
  // console.log('Item created:', response);

  setName(''); 
  setSelectedItems([]); 
  onCancel();
};


const saveCategory = () => {
  // console.log('saveCategory function called');

  if (!validateInputs()) return;

  const dataInput = processInputs(); 
  // console.log ("At SaveCat DataInput", dataInput)

    
    if (isEditing) {
      // console.log('Edit data:', dataInput);
      // Check if we're editing a file or a category
      if (mode === "FILE") {
        // For file edit
        dispatch(submitEditFile({
          ...dataInput,
          docType: dataInput.docType, // Pasing the docType down
          setStateFunc
        }));
      } else {
        // For category edit
        // console.log("Submitting edit with parent ID:", dataInput.parent);
        dispatch(submitEditCategory({
          ...dataInput,
          docType: 'dir',
          setStateFunc
      }));
     }
      
    } else {
      // Use different actions based on mode
      if (mode === "FILE") {
        const file = fileUpload.current?.files;
        // console.log('File to upload:', file);
        dispatch(submitFile({
          ...dataInput,
          docType: 'media-file',
          setStateFunc
        }));
      } else {
        dispatch(submitCreateCategory({
          ...dataInput,
          docType: 'dir',
          setStateFunc
        }));
      }
    }
  };





  const handleFileChange = () => {
    const file = fileUpload.current.files[0];

    if (file) {
        const maxFileSize = 150 * 1024 * 1024;
        const allowedFormats = ['application/pdf', 'image/jpeg', 'image/png'];

        if (!allowedFormats.includes(file.type)) {
            fileUpload.current.value = '';
            setIsFileError(true);
            setIsError(true);
            setErrorMessage("Please upload a PDF, JPG, or PNG file.");
            return;
        }

        if (file.size > maxFileSize) {
            setIsError(true);
            setIsFileError(true);
            setErrorMessage('File size exceeds 150 MB.');
            return;
        }
        
        setFile(file);
        setIsError(false);
        setIsFileError(false);
    } else {
        setIsError(true);
        setIsFileError(true);
        setErrorMessage("Upload file");
        return;
    }
};

const deviceCategories = useMemo(() => {
  const categories = alarmDeviceList.reduce((acc, device) => {
    if (!acc.find(cat => cat.alarm_device_type === device.alarm_device_type)) {
      acc.push({
        alarm_device_type: device.alarm_device_type,
        description: device.description
      });
    }
    return acc;
  }, []);
  
  // Filter out excluded categories from the dropdown
  const normalizedExcluded = excludedCategories.map(cat =>
    String(cat).trim().toLowerCase()
  );
  
  return categories.filter(category => {
    const categoryValue = String(category.alarm_device_type).trim().toLowerCase();
    const categoryDesc = (category.description || '').toLowerCase().trim();
    
    // Check if category type or description is excluded
    const isExcluded = normalizedExcluded.includes(categoryValue) ||
                       normalizedExcluded.some(excluded => categoryDesc === excluded);
    
    return !isExcluded;
  }).sort((a, b) => {
    // Sort alphabetically by description
    const descA = (a.description || '').toLowerCase();
    const descB = (b.description || '').toLowerCase();
    return descA.localeCompare(descB);
  });
}, [alarmDeviceList, excludedCategories]);

// Handle category selection and filter devices
// const handleCategoryChange = (e) => {
//   const categoryValue = e.target.value;
//   // Convert to number if it's a numeric string, otherwise keep as is
//   const parsedValue = /^\d+$/.test(categoryValue) ? parseInt(categoryValue, 10) : categoryValue;
//   setSelectedDeviceCategory(parsedValue);
// };

const handleCategoryChange = (category) => {
  console.log("Category selected for document:", category);
  setSelectedCategory(category);
};


const handleDeviceCategoryChange = (e) => {
  const categoryValue = e.target.value;
  // Convert to number if it's a numeric string, otherwise keep as is
  const parsedValue = /^\d+$/.test(categoryValue) ? parseInt(categoryValue, 10) : categoryValue;
  setSelectedDeviceCategory(parsedValue);
};

const hasCatmoLineage = useMemo(() => {
    if (!data) return false;
    if (!data.hasCatmoLineage) {
      // If not directly provided, check if it's a child of Catmo
      if (data.parent) {
        const parentId = typeof data.parent === 'object' ? data.parent.id : data.parent;
        return parentId === catmoNodeId || data.parent === 'catmo' || 
          (typeof data.parent === 'object' && data.parent.name && 
           data.parent.name.toLowerCase() === 'catmo');
      }
    }
    return data.hasCatmoLineage || false;
  }, [data, catmoNodeId]);

const getFilenameFromPath = (filePath, fallbackName) => {
  if (!filePath) return fallbackName || 'No file chosen';

  if (typeof filePath === 'string') {
    return filePath.split('/').pop().split('\\').pop();
  } else if (filePath.name) {
    return filePath.name;
  } else if (filePath.file_name) {
    return filePath.file_name;
  } else if (filePath.url) {
    return filePath.url.split('/').pop().split('\\').pop();
  }

  return fallbackName || 'Unknown file';
};

// console.log('isEditing:', isEditing, 'mode:', mode, 'data:', data);

  return (
    <div className="modal-overlay">
      <div className="modal-content-container add-category-modal">
       <div className="modal-title category-modal-title" data-testid="category-popup-title">
          {title}
       </div>
       {!hasCatmoLineage && mode === "CATEGORY" && !isChild && !isEditing && !hasOnlyAssociatePermission && (
          <p className="small-text">
            Define a category name. After clicking "Save" you can add subcategories and images to this category.
          </p>
        )}
        {!hasCatmoLineage && mode === "CATEGORY" && isChild && !isEditing && (
          <p className="small-text">
            Define a subcategory name and select its parent category.
          </p>
        )}
        {!hasCatmoLineage && mode === "CATEGORY" && isEditing && !hasOnlyAssociatePermission && (
        <p className="small-text">
          {isChild
            ? 'Please rename your subcategory, change the category it is nested under, or add/remove devices and click "Update".'
            : 'Please rename your category or add/remove devices and click "Update".'}
        </p>
        )}
         {!hasCatmoLineage && mode === "FILE" && !isEditing && !hasOnlyAssociatePermission && (
          <p className="small-text">
          Name the document or image, choose a category to assign it to, and upload your file.
        </p>
        )}
        {!hasCatmoLineage && mode === "FILE" && isEditing && !hasOnlyAssociatePermission && (
        <p className="medium-text">Only upload a new file if you want to replace the existing one.</p>
        )}
        {(hasCatmoLineage || hasOnlyAssociatePermission) && (
        <p className="small-text warning-text" style={{ color: '#e74c3c', fontWeight: 'bold' }}>
          {hasCatmoLineage ? (
            mode === "FILE" 
              ? "This file belongs to the Catie Mobile and cannot be modified from Sara. You may only associate it with devices."
              : "This category is part of Catie Mobile integration and cannot be renamed or deleted in Sara. You may only associate it with devices."
          ) : (
            mode === "FILE"
              ? "You have limited permissions. You may only associate this file with devices."
              : "You have limited permissions. You may only associate this category with devices."
          )}
        </p>
      )}
      <div className="group-name-container category-name-container">
        <label
          htmlFor="categoryName"
          className={isNameError ? 'group-filter-container-title highLight' : 'group-filter-container-title'}
          data-testid="category-input-lable"
          id='label-name'
        >
         {mode === "FILE" ? "Document/Image Name" : "Category Name"} <span className="highLight">*</span>
        </label>
          <input
            id="categoryName"
            type="text"
            placeholder={mode === 'FILE' ? 'File Name' : 'Category Name'}
            className={`event-input-field`}
            value={name}
            onChange={(e) => {
            validateInputChange(e, setName, setErrorMessage, "categoryName");
            setIsNameError(false);
            setName(e.target.value);
            setIsNameError(false);
            }}
            disabled={hasCatmoLineage || hasOnlyAssociatePermission}
            title={(hasCatmoLineage || hasOnlyAssociatePermission) ? 
              (hasCatmoLineage ? 
                "This category is part of Catie Mobile integration and cannot be renamed in Sara" : 
                "You have limited permissions and cannot modify this field"
              ) : ""}
            
          />
        </div>

        {isChild && mode === "CATEGORY" && (
          <div className="category-select-container">
            <div 
              className="group-filter-container-title" 
              data-testid="parent-dropdown-label" 
              id="parent-select-title"
            > 
              Nested under
            </div>
            {(hasCatmoLineage || hasOnlyAssociatePermission) && <span style={{ fontStyle: 'italic' }}> (Disabled)</span>}
            <div className="group-filter-inner-container category-filter-container">
              <CategoryDropdown
                categoriesList={categoriesList}
                isCategory={true}
                selectedCategory={selectedCategory}
                setSelectedCategory={handleCategoryChange}
                excludeCategoryName="catmo"
                disabled={hasCatmoLineage || hasOnlyAssociatePermission}
              />
            </div>
          </div>
        )}

        { mode === "FILE" && (!props.isCategory && categoriesList.categories.length > 0) &&
          <div className="category-select-container">
             <div 
              className="group-filter-container-title" 
              data-testid="category-dropdown-lable" 
              id="category-select-title"
              > Parent Category </div>
              {(hasCatmoLineage || hasOnlyAssociatePermission) && <span style={{ fontStyle: 'italic' }}> (Disabled)</span>}
            <div className="group-filter-inner-container category-filter-container">
              <CategoryDropdown 
              categoriesList={categoriesList}
              isCategory={props.isCategory}  
              selectedCategory={selectedCategory}
              setSelectedCategory={handleCategoryChange}
              excludeCategoryName="catmo"
              disabled={hasCatmoLineage || hasOnlyAssociatePermission}
            />                   
           </div>
         </div>}

         <div className='file-upload-ctr' style={mode === "FILE" ? {visibility: "visible"} : {visibility: "hidden"}}>
          <div className="file-input-wrapper" style={{ position: 'relative' }}>
            <input
              className={isFileError ? 'warn-msg' : ''}
              type="file"
              id="fileInput"
              data-testid="file-upload-input"
              accept=".pdf, .jpg, .png"
              onChange={handleFileChange}
              ref={fileUpload}
              disabled={hasCatmoLineage || hasOnlyAssociatePermission}
              style={isEditing && currentFile ? { 
                // Hide the original input if we're showing the custom label
                opacity: 0, 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                zIndex: 2
              } : {}}
            />

            {/* Custom file input display for edit mode */}
            {isEditing && currentFile && (
              <div className="file-input-wrapper" style={{
                display: 'flex',
                border: '1px solid #ccc',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <button
                  className="choose-file-btn"
                  disabled={hasCatmoLineage || hasOnlyAssociatePermission}
                  title={hasCatmoLineage ? "This file belongs to Catie Mobile and cannot be replaced in Sara" : ""}
                  style={{
                    backgroundColor: (hasCatmoLineage || hasOnlyAssociatePermission) ? '#9bb8d6' : '#007bff',
                    color: 'white',
                    padding: '6px 12px',
                    border: 'none',
                    cursor: (hasCatmoLineage || hasOnlyAssociatePermission) ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => {
                    if (hasCatmoLineage || hasOnlyAssociatePermission) return;
                    document.getElementById('fileInput').click();
                  }}
                >
                  Choose File
                </button>
                <span 
                  className="file-name" 
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'white',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {getFilenameFromPath(currentFile, data.name)}
                </span>
              </div>
            )}
          </div>
            <p data-testid="file-upload-info" className={isFileError ? 'highLight' : ''} style={{fontSize:"10px", marginTop:"5px"}}>{"(Supported Formats : PDF, JPG, PNG)"}</p>
          </div>


          <div className="mb-6">
            <label className="block mb-2 medium-text">
              {mode === "FILE" 
                ? "Note: Files inherit device associations from their category by default, but you may override them at the file level." 
                : "Associate a Device Category or Monitored Device"
              }
            </label>
            
            
              <select 
                className="group-filter-inner-container category-filter-container"
                value={selectedDeviceCategory}
                onChange={handleDeviceCategoryChange}
              >
                <option value="">Filter by Device Category</option>
                {deviceCategories.map(category => (
                  <option 
                    key={category.alarm_device_type} 
                    value={category.alarm_device_type}
                  >
                    {category.description}
                  </option>
                ))}
              </select>
            
          </div>

        <div className="group-management-container category-group-management-container">
          <div className="create-alert-selection-container">
            {/* Left Panel */}
           <div className='card-container'>
              <div className="item-listing-box">
               <div className="card-container-head card-select-box-left small-text">
                <span>Device Category/Monitored Device</span>
             </div>
          
            {/* {mode === "CATEGORY" && ( */}
             <div className="card-filter">
              <input
                type="text"
                placeholder="Search"
                className="search-contaner"
                value={leftSearchValue}
                onChange={(e) => setLeftSearchValue(e.target.value)}
              />
            </div>
          
          
            <div className="card-data-list">
              {getVisibleDevices().map((device, index) => (
                <div
                  key={index}
                  className="p-2 cursor-pointer flex"
                  style={{
                    backgroundColor: selectedLeftItem && getDeviceIdentifier(selectedLeftItem) === getDeviceIdentifier(device)
                      ? '#448CCB' 
                      : 'transparent'
                  }}
                  onMouseLeave={(e) => {
                    if (!(selectedLeftItem && getDeviceIdentifier(selectedLeftItem) === getDeviceIdentifier(device))) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => {
                    if (selectedLeftItem && getDeviceIdentifier(selectedLeftItem) === getDeviceIdentifier(device)) {
                      setSelectedLeftItem(null);
                    } else {
                      setSelectedLeftItem(device);
                      setSelectedRightItem(null);
                    }
                  }}
                  onDoubleClick={() => handleTransfer(device)} // Double-click for immediate transfer
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {device.isSpecificDevice 
                        ? (device.alarm_description || "Unnamed Device")
                        : (device.description || "Unnamed Type")}
                      {" "}
                      ({device.isSpecificDevice 
                        ? device.serial_id && /\d{6}$/.test(device.serial_id)
                          ? device.serial_id.slice(-6) 
                          : (device.alarm_device_id || 'unknown')
                        : (device.alarm_device_type || device.type || 'unknown')})
                    </span>
                  </div>
                </div>
              ))}
            </div>
        </div>
      </div>

            {/* Transfer Buttons */}
          {/* {mode === "CATEGORY" && ( */}
            <div className="user-list-option-container-list">
              <button 
                className={`mb-2 ${selectedLeftItem ? '' : 'opacity-50 cursor-not-allowed'}`}
                onClick={handleMoveRight}
                disabled={!selectedLeftItem}
                title={selectedLeftItem ? `Move "${selectedLeftItem.description || selectedLeftItem.alarm_description}" to assigned` : 'Select a device from the left to move'}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button 
                className={selectedRightItem ? '' : 'opacity-50 cursor-not-allowed'}
                onClick={handleMoveLeft}
                disabled={!selectedRightItem}
                title={selectedRightItem ? `Remove "${selectedRightItem.description || selectedRightItem.alarm_description}" from assigned` : 'Select a device from the right to remove'}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>

            {/* Right Panel */}
        <div className='card-container'>
         <div className="item-listing-box">
          <div className="card-container-head card-select-box-right small-text">
            <span>Assigned</span>
          </div>
          
            
            <div className="card-filter">
              <input
                type="text"
                placeholder="Search"
                className="search-contaner"
                value={rightSearchValue}
                onChange={(e) => setRightSearchValue(e.target.value)}
              />
            </div>
           
          
           <div className="card-data-list">
            {/* Show inherited devices for files */}
            {selectedItems
              .filter(item => {
                // Safety check first
                if (!item) return false;
                
                // Only apply filter in CATEGORY mode
                if (mode === "CATEGORY") {
                  const searchTerm = rightSearchValue.toLowerCase();
                  return (
                    (item.description && item.description.toLowerCase().includes(searchTerm)) ||
                    (item.alarm_description && item.alarm_description.toLowerCase().includes(searchTerm))
                  );
                }

                return true;
              })
              .map((item) => (
          <div
            key={getDeviceIdentifier(item)}
            className={`p-2 ${mode === "CATEGORY" ? "cursor-pointer" : ""} flex`}
            style={{
              backgroundColor: selectedRightItem && getDeviceIdentifier(selectedRightItem) === getDeviceIdentifier(item)
                ? '#448CCB' 
                : 'transparent'
            }}
            
            onMouseLeave={(e) => {
              if (!(selectedRightItem && getDeviceIdentifier(selectedRightItem) === getDeviceIdentifier(item))) {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => {
              // Toggle selection
              if (selectedRightItem && getDeviceIdentifier(selectedRightItem) === getDeviceIdentifier(item)) {
                setSelectedRightItem(null); 
              } else {
                setSelectedRightItem(item); 
                setSelectedLeftItem(null); 
              }
            }}
            onDoubleClick={() => handleTransfer(item)} // Double-click to transfer
          >
            <div className="flex flex-col">
              <span className="font-medium">
                {item.description || item.alarm_description || "Unnamed"}{" "}
                ({item.isSpecificDevice
                  ? item.serial_id && /\d{6}$/.test(item.serial_id)
                    ? item.serial_id.slice(-6)
                    : (item.alarm_device_id || 'unknown')
                  : (item.alarm_device_type || item.type || 'unknown')})
              </span>
            </div>
          </div>
        ))}
        {/*message if no devices are assigned */}
        {selectedItems.length === 0 && (
              <div className="p-2 text-gray-500">
                {mode === "CATEGORY" 
                  ? "No devices assigned. Select devices from the left panel."
                  : "No devices are inherited from the parent category."
                }
              </div>
              )}
            </div>
          </div>
        </div>
        
            
          
        
        </div>
        </div>
        <div className='response-container' data-testid="category-popup-error-message-ctr" style={{ flex: '1 0 auto' }}>{isError ? errorMessage : " "}</div>
        <div className="popup-button-container category-button-container">
        <button 
          className="cancel-button"
          onClick={(e) => {
            e.preventDefault();
            onCancel()
          }}>Cancel</button>
        {/* <div className="vertical_line"></div> */}
        <button 
          className="save-button"  
          onClick={() => {
            // console.log('Save button clicked');
            saveCategory();
          }}
        >
          {hasOnlyAssociatePermission ? 'Associate Devices' : (isEditing ? 'Update' : 'Save')}
        </button>
        </div>
      </div>
    </div>
  );
};

CategoryFileManager.propTypes = {
  data: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      label: PropTypes.string,
      isGroupRestriction: PropTypes.bool,
      parentId: PropTypes.number,
      groupIds: PropTypes.array,
      displayOrder: PropTypes.number,
      isInherited: PropTypes.bool,
  }),
  isChild: PropTypes.bool,
  isCategory: PropTypes.bool,
  mode: PropTypes.string,
  isEditing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func,

};

export default CategoryFileManager;